/**
 * ---------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 * ---------------------------------------------------------------------------------------------
 */

import * as microsoftTeams from '@microsoft/teams-js';
import React, { Component } from 'react';
import { getContext, TeamsThemeContext, ThemeStyle } from 'msteams-ui-components-react';
import Main from './components/main';
import messages from './locale/en';
import { isInTeams } from './utils/iframe';
import { initializeIcons } from "office-ui-fabric-react/lib/Icons";
import { IntlProvider } from 'react-intl';
import { IValidationTelemetryProperties, ValidationConstants } from './utils/ITelemetry';
import { sendTelemetry } from './utils/sendTelemetry';

interface IAppValidationState {
  fontSize: number;
  theme: ThemeStyle
}

export default class AppValidation extends Component<{}, IAppValidationState> {

  constructor(props: any) {
    super(props);

    // Initializing the office-ui-fabric-icons here to avoid multiple initializations in every component.
    initializeIcons();
  }

  public state: IAppValidationState = {
    fontSize: 16,
    theme: ThemeStyle.Light
  };

  public async componentWillMount(): Promise<void> {
    this.updateTheme(this.getQueryVariable('theme'));

    if (isInTeams()) {
      microsoftTeams.initialize();
      microsoftTeams.appInitialization.notifySuccess();
      microsoftTeams.registerOnThemeChangeHandler(this.updateTheme);
    }
  }

  public componentDidMount() {
    let appValidationLoadedPropterties: IValidationTelemetryProperties = {
      tool: ValidationConstants.tool,
      event: ValidationConstants.onLoad
    }

    sendTelemetry(ValidationConstants.message, appValidationLoadedPropterties)
  }

  public render(): JSX.Element {
    const context = getContext({
      baseFontSize: this.state.fontSize,
      style: this.state.theme
    });

    return (
      <IntlProvider locale="en" messages={messages} defaultLocale="en">
        <TeamsThemeContext.Provider value={context}>
          <Main />
        </TeamsThemeContext.Provider>
      </IntlProvider>
    );
  }

  private updateTheme = (themeStr?: string | null): void => {
    let theme: number;
    switch (themeStr) {
      case 'dark':
        theme = ThemeStyle.Dark;
        break;
      case 'contrast':
        theme = ThemeStyle.HighContrast;
        break;
      case 'default':
      default:
        theme = ThemeStyle.Light;
    }
    this.setState({ theme });
  }

  private getQueryVariable = (variable: string): string | null => {
    const query: string = window.location.search.substring(1);
    const vars: string[] = query.split('&');
    for (const varPairs of vars) {
      const pair: string[] = varPairs.split('=');
      if (decodeURIComponent(pair[0]) === variable) {
        return decodeURIComponent(pair[1]);
      }
    }

    return null;
  }
}
